import React, { useState, useEffect } from 'react';
import { FaFileInvoice, FaCheckCircle, FaTimesCircle, FaClock, FaRupeeSign, FaDollarSign, FaGlobe, FaMapMarkerAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/SocketContext';
import PageHeader from '../../components/PageHeader';
import { PageHeaderSkeleton, TableSkeleton } from '../../components/ui/Skeleton';
import EmptyState from '../../components/ui/EmptyState';
import { usePortalContent, getPortalIcon, getAccountPageMeta } from '../../portal';

const PaymentHistory = () => {
    const { currentThemeColors } = useTheme();
    const { content: paymentContent } = usePortalContent('account-payment-history');
    const pageMeta = getAccountPageMeta(paymentContent, 'payment-history');
    const PaymentIcon = getPortalIcon(pageMeta.icon);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchPayments = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }

            try {
                const userResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (!userResponse.ok) throw new Error('Failed to fetch user data');
                const userData = await userResponse.json();

                const paymentsResponse = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/api/payment/user-payments/${userData.user._id}`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );

                if (!paymentsResponse.ok) throw new Error('Failed to fetch payment history');
                const paymentsData = await paymentsResponse.json();

                setPayments(paymentsData);
            } catch (error) {
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchPayments();
    }, [navigate]);

    const formatDate = (dateString) => {
        const options = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'completed':
                return (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                        <FaCheckCircle className="w-3 h-3" /> Completed
                    </span>
                );
            case 'failed':
                return (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
                        <FaTimesCircle className="w-3 h-3" /> Failed
                    </span>
                );
            default:
                return (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400">
                        <FaClock className="w-3 h-3" /> Pending
                    </span>
                );
        }
    };

    const paymentHeader = (
        <PageHeader
            icon={PaymentIcon}
            title={pageMeta.title}
            subtitle={pageMeta.subtitle}
        />
    );

    if (loading) {
        return (
            <div className="space-y-6 animate-fade-in">
                <PageHeaderSkeleton />
                <TableSkeleton rows={5} columns={6} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-6 animate-fade-in">
                {paymentHeader}
                <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 opacity-0 animate-fade-in-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
                    <p className="text-red-600 dark:text-red-400">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {paymentHeader}

            {/* Payment Content */}
            {payments.length === 0 ? (
                <EmptyState
                    icon={FaFileInvoice}
                    title="No payment history found"
                    description="You haven't made any payments yet"
                />
            ) : (
                <div className="card overflow-hidden">
                    {/* Desktop Table */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-dark-700 border-b border-gray-200 dark:border-dark-600">
                                    <th className="px-6 py-4 text-left table-th">Service</th>
                                    <th className="px-6 py-4 text-left table-th">Date & Time</th>
                                    <th className="px-6 py-4 text-left table-th">Amount</th>
                                    <th className="px-6 py-4 text-left table-th">Details</th>
                                    <th className="px-6 py-4 text-left table-th">Order ID</th>
                                    <th className="px-6 py-4 text-left table-th">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-dark-600">
                                {payments.map((payment) => (
                                    <tr key={payment._id} className="hover:bg-gray-50 dark:hover:bg-dark-700/50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-medium text-dark-800 dark:text-white">
                                                    {payment.service || 'Unknown Service'}
                                                </span>
                                                {(payment.serviceState || (payment.service === 'Registered Agent' && payment.state)) && (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-800/40">
                                                        {payment.serviceState || payment.state}
                                                        {payment.slot ? ` (Slot ${payment.slot})` : ''}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                                            {formatDate(payment.createdAt)}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-1 font-semibold text-dark-800 dark:text-white">
                                                {payment.currency === 'INR' ? <FaRupeeSign className="w-3.5 h-3.5" /> : <FaDollarSign className="w-3.5 h-3.5" />}
                                                {payment.displayAmount || payment.amount.toFixed(2)}
                                            </div>
                                            {payment.gstApplied && (
                                                <span className="text-xs text-gray-500 dark:text-gray-400">(incl. GST)</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                                                {payment.location === 'india' ? (
                                                    <><FaMapMarkerAlt className="w-3.5 h-3.5" /> India</>
                                                ) : (
                                                    <><FaGlobe className="w-3.5 h-3.5" /> International</>
                                                )}
                                            </div>
                                            {payment.gstApplied && (
                                                <span className="text-xs text-gray-500 dark:text-gray-400">+18% GST</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <code className="text-xs bg-gray-100 dark:bg-dark-700 px-2 py-1 rounded text-gray-600 dark:text-gray-400">
                                                {payment.orderId}
                                            </code>
                                        </td>
                                        <td className="px-6 py-4">
                                            {getStatusBadge(payment.status)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="bg-gray-50 dark:bg-dark-700 border-t border-gray-200 dark:border-dark-600">
                                    <td colSpan={6} className="px-6 py-3 text-right text-sm font-medium text-gray-600 dark:text-gray-400">
                                        Total: {payments.length} payment{payments.length !== 1 ? 's' : ''}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    {/* Mobile Cards */}
                    <div className="md:hidden p-4 space-y-3">
                        {payments.map((payment) => (
                            <div key={payment._id} className="p-4 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600">
                                <div className="flex items-start justify-between mb-3">
                                    <div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <p className="font-semibold text-dark-800 dark:text-white">
                                                {payment.service || 'Unknown Service'}
                                            </p>
                                            {(payment.serviceState || (payment.service === 'Registered Agent' && payment.state)) && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-800/40">
                                                    {payment.serviceState || payment.state}
                                                    {payment.slot ? ` (Slot ${payment.slot})` : ''}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            {formatDate(payment.createdAt)}
                                        </p>
                                    </div>
                                    {getStatusBadge(payment.status)}
                                </div>
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <p className="text-gray-500 dark:text-gray-400 text-xs mb-1">Amount</p>
                                        <p className="font-semibold text-dark-800 dark:text-white flex items-center gap-1">
                                            {payment.currency === 'INR' ? <FaRupeeSign className="w-3 h-3" /> : <FaDollarSign className="w-3 h-3" />}
                                            {payment.displayAmount || payment.amount.toFixed(2)}
                                        </p>
                                        {payment.gstApplied && (
                                            <span className="text-xs text-gray-500">(incl. GST)</span>
                                        )}
                                    </div>
                                    <div>
                                        <p className="text-gray-500 dark:text-gray-400 text-xs mb-1">Location</p>
                                        <p className="text-dark-800 dark:text-white flex items-center gap-1">
                                            {payment.location === 'india' ? (
                                                <><FaMapMarkerAlt className="w-3 h-3" /> India</>
                                            ) : (
                                                <><FaGlobe className="w-3 h-3" /> International</>
                                            )}
                                        </p>
                                    </div>
                                </div>
                                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-dark-600">
                                    <p className="text-gray-500 dark:text-gray-400 text-xs mb-1">Order ID</p>
                                    <code className="text-xs bg-white dark:bg-dark-800 px-2 py-1 rounded text-gray-600 dark:text-gray-400">
                                        {payment.orderId}
                                    </code>
                                </div>
                            </div>
                        ))}
                        <p className="text-right text-sm text-gray-500 dark:text-gray-400 pt-2">
                            Total: {payments.length} payment{payments.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentHistory;